<template>
  <div class="row" id="EventViewer">
    <!--chart-->
    <div class="col s9">
      <div class="col s12 z-depth-1 white" style="padding-top: 10px">
        <div id="timelineChart" class="row no-margin-bottom chart-container" ref="timelineChart">
          <div class="vue-highcharts-wrapper" v-if="this.tooMuch==false">
            <HighChart
              ref="chartRef"
              :options="chartOptions"
              :class="['cssChart', loading ? 'highcharts-loading' : '']"/>
          </div>
          <div class="csstooMuch" v-if="this.tooMuch==true">
              {{ $t('views.switch.tomuchvalues') }}
          </div>
          <div class="spinner-overlay" v-if="loading">
            <Spinner />
          </div>
        </div>
      </div>
    </div>
    <!--controls-->
    <div class="col s3">
      <div class="col s12 z-depth-1 white" style="height: 265px;">
        <div class="input-field">
          <button class="waves-effect waves-light btn buttonbg button-frame" @click="prevFrame">
            <i class="material-icons">
              keyboard_arrow_left
            </i>
          </button>
          <DatePicker v-model="dateModel" :max-date="maxDate" @dateChanged="datePickerChanged" />
          <button class="waves-effect waves-light btn buttonbg button-frame" @click="nextFrame">
            <i class="material-icons">
              keyboard_arrow_right
            </i>
          </button>
          <PeriodSelector v-model="selectedPeriod" class="periodSelector" />
        </div>
        <div class="input-field">
          <select v-model="filterSettings.y1" class="browser-default">
            <option v-for="(metric, index) in filterSettings.y1List" :value="index" :key="index">
              {{ metric.label }}
            </option>
          </select>
        </div>
        <div class="input-field">
          <select v-model="filterSettings.y2" class="browser-default">
            <option v-for="(metric, index) in filterSettings.y2List" :value="index" :key="index">
              {{ metric.label }}
            </option>
          </select>
        </div>
        <div class="input-field">
          <!--<span class="filter-label"-->
          <!--style="padding-right: 20px;">{{ $t('views.switch.switchevents.filter.direction') }}:</span>-->
          <ul class="horizontal-list filter-graph-list">
            <li class="horizontal-list-item filter-graph-list-item">
              <label class="checkbox-small">
                <input type="checkbox" name="directionNormal" v-model="filterSettings.normal" />
                <span class="checkbox-small body1">{{ $t('views.switch.directionnormal') }}</span>
              </label>
            </li>
            <li class="horizontal-list-item filter-graph-list-item">
              <label class="checkbox-small">
                <input type="checkbox" name="directionReverse" v-model="filterSettings.reverse" />
                <span class="checkbox-small body1">{{ $t('views.switch.directionreverse') }}</span>
              </label>
            </li>
          </ul>
          <div class="col s5">
            <div class="waves-effect waves-light btn-flat graph-mode-selector-button">
              <select v-model="graphMode" class="browser-default graph-mode-selector">
                <option value="combined" style="color: black;">{{ $t('views.switch.charts.combined') }}</option>
                <option value="split" style="color: black;">{{ $t('views.switch.charts.split') }}</option>
              </select>
            </div>
            <br>
            <a v-show="selected.length > 0" @click="clearSelection" class="actionLink">
              {{ $t('events.clearSelected') }}
            </a>
          </div>
          <div class="col s7"
            :title="visible.length === 0 || visible.length > visiblePlotLimit ? $t('events.visiblePlotLimit', [visiblePlotLimit]) : ''">
            <button class="waves-effect waves-light btn buttonbg"
              :disabled="visible.length === 0 || visible.length > visiblePlotLimit || isParent" @click="selectAllVisible">
              <i class="material-icons redo">redo</i>
              {{ $t('events.plotAllVisible') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import { Chart } from 'highcharts-vue'
import moment from 'moment'
import rca from 'rainbow-colors-array'
import DatePicker from './DatePicker'
import PeriodSelector from '.././PeriodSelector'
import Highcharts from 'highcharts'
import Spinner from '@/components/Spinner'

export default {
  name: 'TimeSelector',
  components: {
    DatePicker,
    HighChart: Chart,
    PeriodSelector,
    Spinner
  },
  props: {
    measurements: {
      required: true
    },
    timeFrame: {
      required: true
    },
    selected: {
      required: false,
      default: []
    },
    activeMeasurement: {
      required: false
    },
    refHistory: {
      required: false
    },
    maxDate: {
      required: false,
      default: new Date()
    },
    isParent: {
      required: false,
      default: false
    },
    switchObject: {
      required: true
    }
  },
  data () {
    const MAXNORM = 25000
    const MAXWARNS = 5000
    const MAXALARMS = 5000
    const MAXY2 = 35000
    return {
      tooMuch: false,
      loading: false,
      maxCountNormal: MAXNORM,
      maxCountWarnings: MAXWARNS,
      maxCountAlarms: MAXALARMS,
      maxCountY2Data: MAXY2,
      maxCountTotal: MAXNORM + MAXWARNS + MAXALARMS + MAXY2,
      dateModel: this.timeFrame,
      normalMeasurements: [],
      warningMeasurements: [],
      alarmMeasurements: [],
      filterSettings: {
        normal: true,
        reverse: true,
        min: 0,
        max: 0,
        y1List: [
          { label: this.$t('views.switch.switchevents.filter.direction'), prop: 'Direction' },
          { label: this.$t('views.switch.temperature') + ' (℃)', prop: 'Temperature' },
          { label: this.$t('views.switch.duration') + ' (ms)', prop: 'Duration' },
          { label: this.$t('views.switch.power'), prop: 'TotalPower' },
          { label: this.$t('views.switch.details.trendpowerperfdiff') + ' (%)', prop: 'PowerDiffPerc' }
        ],
        y2List: [
          { label: this.$t('views.switch.temperature') + ' (℃)', prop: 'Temperature' },
          { label: this.$t('views.switch.duration') + ' (ms)', prop: 'Duration' },
          { label: this.$t('views.switch.power'), prop: 'TotalPower' },
          { label: this.$t('views.switch.details.trendpowerperfdiff') + ' (%)', prop: 'PowerDiffPerc' }
        ],
        y1: 0,
        y2: 0
      },
      visiblePlotLimit: 20,
      chartOptions: this.createChartOptions(this),
      selectedPeriod: moment(this.timeFrame.end).isSame(moment(), 'day') ? '1week' : 'user_input',
      graphMode: localStorage.graphMode
    }
  },
  computed: {
    toMuch () {
      return this.normalMeasurements.length > this.maxCountNormal ||
        this.warningMeasurements.length > this.maxCountWarnings ||
        this.alarmMeasurements.length > this.maxCountAlarms ||
        (this.normalMeasurements.length + this.warningMeasurements.length + this.alarmMeasurements.length) > this.maxCountY2Data
    },
    span () {
      return Math.max(1, Math.abs(moment(this.timeFrame.end).diff(this.timeFrame.start, 'days')))
    },
    visible () {
      if (this.filterSettings.min !== 0 && this.filterSettings.max !== 0) {
        return this.measurements.filter(m => {
          const epoch = this.$options.filters.epoch(m.MeasurementDateTime)
          return epoch >= this.filterSettings.min && epoch <= this.filterSettings.max
        }).filter(f => {
          return ((f.Direction === 0 && this.filterSettings.normal) || (f.Direction === 1 && this.filterSettings.reverse))
        })
      } else {
        return this.measurements
      }
    },
    colors () {
      return rca(Math.max(8, this.selectedUnique.length), 'hex').map(x => '#' + x.hex) // pre-load a rca array of at least 8
    },
    selectedUnique () {
      let result = []
      result = [...new Set(this.selected.filter(s => s.children.length > 0).map(s => s.id))]

      // need to create a result in case we're on the lowest level
      if (result.length === 0) {
        result = [...new Set(this.selected.map(s => s.id))]
      }
      // result = result.sort((a, b) => b - a) // do not sort, we need an unsorted list dor the colors
      return result
    }
  },
  methods: {
     // in methods
     // Usage: call this after formatData() or whenever measurements change
    checkChartLoad () {
      const thresholds = {
        maxNormal: this.maxCountNormal,
        maxWarn: this.maxCountWarnings,
        maxAlarm: this.maxCountAlarms,
        maxTotal: this.maxCountTotal,
        bytesPerPoint: 40
      }
      const counts = {
        normal: Array.isArray(this.normalMeasurements) ? this.normalMeasurements.length : 0,
        warn: Array.isArray(this.warningMeasurements) ? this.warningMeasurements.length : 0,
        alarm: Array.isArray(this.alarmMeasurements) ? this.alarmMeasurements.length : 0,
        y2: (this.chartOptions && this.chartOptions.series && this.chartOptions.series[3] && this.chartOptions.series[3].data) ? this.chartOptions.series[3].data.length : 0
      }
      const total = counts.normal + counts.warn + counts.alarm + counts.y2
      let chartPx = 1000
      if (this.$refs.chartRef && this.$refs.chartRef.chart && this.$refs.chartRef.chart.chartWidth) {
        chartPx = this.$refs.chartRef.chart.chartWidth
      } else if (this.$refs.timelineChart && this.$refs.timelineChart.clientWidth) {
        chartPx = this.$refs.timelineChart.clientWidth
      } else {
        const el = document.getElementById('timelineChart')
        if (el && el.clientWidth) chartPx = el.clientWidth
      }
      const pointsPerPixel = chartPx > 0 ? total / chartPx : Infinity
      const estimatedMemoryKB = (total * thresholds.bytesPerPoint) / 1024
      const perSeriesExceeded = {
        normal: counts.normal > thresholds.maxNormal,
        warn: counts.warn > thresholds.maxWarn,
        alarm: counts.alarm > thresholds.maxAlarm
      }
      const tooMuch = perSeriesExceeded.normal || perSeriesExceeded.warn || perSeriesExceeded.alarm || total > thresholds.maxTotal
      this.tooMuch = tooMuch
      return (this._chartLoadDiagnostics = {
        counts,
        total,
        chartPx,
        pointsPerPixel,
        estimatedMemoryKB,
        perSeriesExceeded,
        thresholds
      })
    },
    createChartOptions (t) {
      return {
        chart: {
          height: 250,
          zoomType: 'x',
          resetZoomButton: {
            position: {
              x: 0,
              y: -10
            }
          },
          animation: false,
          zooming: {
            mouseWheel: {
              enabled: true
            }
          },
          events: {
            selection (e) {
              if (!e.resetSelection) {
                t.filterSettings.min = e.xAxis[0].min
                t.filterSettings.max = e.xAxis[0].max
              } else {
                t.filterSettings.min = 0
                t.filterSettings.max = 0
              }
            },
            load () {
              const chart = this

              // Block scroll wheel unless Shift is held
              chart._wheelBlocker = (e) => {
                if (!e.shiftKey) {
                  if (e.stopImmediatePropagation) e.stopImmediatePropagation()
                  e.stopPropagation()
                }
              }
              chart.container.addEventListener('wheel', chart._wheelBlocker, { capture: true })

              // Show zoom cursor when Shift is held
              chart._cursorHint = (e) => {
                chart.container.style.cursor = e.shiftKey ? 'zoom-in' : ''
              }
              chart.container.addEventListener('mousemove', chart._cursorHint)

              // Flip cursor immediately on Shift press/release
              chart._onKeyShift = (e) => {
                if (e.key === 'Shift' && chart.container) {
                  chart.container.style.cursor = (e.type === 'keydown') ? 'zoom-in' : ''
                }
              }
              chart._offKeyDown = Highcharts.addEvent(document, 'keydown', chart._onKeyShift)
              chart._offKeyUp = Highcharts.addEvent(document, 'keyup', chart._onKeyShift)
            },
            destroy () {
              const el = this.container
              if (this._wheelBlocker && el) el.removeEventListener('wheel', this._wheelBlocker, { capture: true })
              if (this._cursorHint && el) el.removeEventListener('mousemove', this._cursorHint)
              if (this._offKeyDown) this._offKeyDown()
              if (this._offKeyUp) this._offKeyUp()
            }
          }
        },
        time: {
          useUTC: true
        },
        accessibility: {
          enabled: false
        },
        exporting: {
          enabled: false
        },
        title: {
          text: ''
        },
        credits: {
          enabled: false
        },
        subtitle: {
          text: ''
        },
        legend: {
          enabled: true
        },
        tooltip: {
          formatter: function () {
            let s = moment.utc(this.x).format('YYYY-MM-DD HH:mm:ss') + '<br/>' +
              t.$t('views.switch.switchevents.filter.direction') + ': <b>' +
              (this.y ? t.$t('views.switch.directionreverse') : t.$t('views.switch.directionnormal')) + '</b>'

            switch (t.filterSettings.y2) {
              case 0:
                s += '<br/>' + t.$t('views.switch.temperature') + ': <b>' + (this.point.t || '-') + '°C</b>'
                break
              case 1:
                s += '<br/>' + t.$t('views.switch.duration') + ': <b>' + (this.point.t || '-') + ' ms</b>'
                break
              case 2:
                s += '<br/>' + t.$t('views.switch.power') + ': <b>' + (this.point.t || '-') + '</b>'
                break
              case 3:
                s += '<br/>' + t.$t('views.switch.details.trendpowerperfdiff') + ': <b>' + (this.point.t || '-') + '%</b>'
                break
            }

            return s
          },
          shared: true,
          crosshairs: true
        },
        lang: {
          noData: this.$t('views.switch.timeselector.nodata')
        },
        xAxis: {
          title: {
            enabled: false
          },
          type: 'datetime',
          gridLineWidth: 1,
          dateTimeLabelFormats: {
            millisecond: '%H:%M:%S.%L',
            second: '%H:%M:%S',
            minute: '%H:%M',
            hour: '%H:%M',
            day: '%e %b \'%y',
            week: '%e %b \'%y',
            month: '%b \'%y',
            year: '%Y'
          }
        },
        yAxis: [
          {
            lineWidth: 1,
            categories: null,
            title: {
              text: ''
            }
          },
          {
            lineWidth: 1,
            opposite: true,
            title: {
              text: ''
            }
          }
        ],
        series: [
          {
            name: this.$t('views.switch.severitynormal'),
            type: 'scatter',
            marker: {
              symbol: 'diamond',
              radius: 3,
              fillColor: 'black'
            },
            zIndex: 3,
            data: [],
            turboThreshold: this.maxCountNormal,
            yAxis: 0,
            stickyTracking: false,
            point: {
              events: {
                click: (e) => {
                  t.switchSelected(e.point.measurementId)
                  return false
                }
              }
            }
          },
          {
            name: this.$t('views.switch.severitywarning'),
            type: 'scatter',
            marker: {
              symbol: 'circle',
              radius: 8,
              fillColor: 'orange'
            },
            zIndex: 1,
            data: [],
            turboThreshold: this.maxCountWarnings,
            yAxis: 0,
            stickyTracking: false,
            point: {
              events: {
                click: (e) => {
                  t.switchSelected(e.point.measurementId)
                  return false
                }
              }
            }
          },
          {
            name: this.$t('views.switch.severityalarm'),
            type: 'scatter',
            marker: {
              symbol: 'triangle',
              radius: 8,
              fillColor: 'red'
            },
            zIndex: 2,
            data: [],
            turboThreshold: this.maxCountAlarms,
            yAxis: 0,
            stickyTracking: false,
            point: {
              events: {
                click: (e) => {
                  t.switchSelected(e.point.measurementId)
                  return false
                }
              }
            }
          },
          {
            name: 'y2',
            type: 'spline',
            color: '#66F',
            opacity: 0.5,
            zIndex: 0,
            data: [],
            turboThreshold: this.maxCountY2Data,
            yAxis: 1,
            stickyTracking: false,
            enableMouseTracking: false
          }
        ]
      }
    },
    formatData () {
      let dataNormal = []
      let dataWarning = []
      let dataAlarm = []
      let dataY2 = []
      const propY1 = this.filterSettings.y1List[this.filterSettings.y1].prop
      const propY2 = this.filterSettings.y2List[this.filterSettings.y2].prop
      this.normalMeasurements.forEach(m => {
        let include = this.filterSettings.normal && m.Direction === 0
        include |= this.filterSettings.reverse && m.Direction === 1
        if (include) {
          // y1
          let index = this.selectedUnique.findIndex(s => s === m.MeasurementId)
          let self = this
          if (m.MeasurementDateTime) {
            dataNormal.push({
              x: self.$options.filters.epoch(m.MeasurementDateTime, m.MeasurementId),
              y: m[propY1],
              t: m[propY2],
              measurementId: m.MeasurementId,
              comment: m.Comment,
              dataLabels: {
                enabled: !!m.Comment,
                formatter () {
                  return '!'
                },
                y: -32,
                align: 'center',
                style: {
                  fontWeight: 'bold',
                  fontSize: '18px'
                },
                verticalAlign: 'top',
                overflow: true,
                crop: false
              },
              marker: {
                fillColor: index >= 0 ? this.colors[index % this.colors.length] : undefined,
                radius: index >= 0 ? 8 : 3
              }
            })
            // y2
            dataY2.push({
              x: self.$options.filters.epoch(m.MeasurementDateTime, m.MeasurementId),
              y: m[propY2],
              measurementId: m.MeasurementId
            })
          }
        }
      })
      this.warningMeasurements.forEach(m => {
        let include = this.filterSettings.normal && m.Direction === 0
        include |= this.filterSettings.reverse && m.Direction === 1
        if (include) {
          // y1
          let index = this.selectedUnique.findIndex(s => s === m.MeasurementId)
          let self = this
          if (m.MeasurementDateTime) {
            dataWarning.push({
              x: self.$options.filters.epoch(m.MeasurementDateTime, m.MeasurementId),
              y: m[propY1],
              t: m[propY2],
              measurementId: m.MeasurementId,
              comment: m.Comment,
              dataLabels: {
                enabled: !!m.Comment,
                formatter () {
                  return '!'
                },
                y: -32,
                align: 'center',
                style: {
                  fontWeight: 'bold',
                  fontSize: '18px'
                },
                verticalAlign: 'top',
                overflow: true,
                crop: false
              },
              marker: {
                fillColor: index >= 0 ? this.colors[index % this.colors.length] : undefined,
                radius: index >= 0 ? 10 : 6,
                lineColor: 'orange',
                lineWidth: 2
              }
            })
            // y2
            dataY2.push({
              x: self.$options.filters.epoch(m.MeasurementDateTime, m.MeasurementId),
              y: m[propY2],
              measurementId: m.MeasurementId
            })
          }
        }
      })
      this.alarmMeasurements.forEach(m => {
        let include = this.filterSettings.normal && m.Direction === 0
        include |= this.filterSettings.reverse && m.Direction === 1
        if (include) {
          // y1
          let index = this.selectedUnique.findIndex(s => s === m.MeasurementId)
          if (m.MeasurementDateTime) {
            let self = this
            dataAlarm.push({
              x: self.$options.filters.epoch(m.MeasurementDateTime, m.MeasurementId),
              y: m[propY1],
              t: m[propY2],
              measurementId: m.MeasurementId,
              comment: m.Comment,
              dataLabels: {
                enabled: !!m.Comment,
                formatter () {
                  return '!'
                },
                y: -32,
                align: 'center',
                style: {
                  fontWeight: 'bold',
                  fontSize: '18px'
                },
                verticalAlign: 'top',
                overflow: true,
                crop: false
              },
              marker: {
                fillColor: index >= 0 ? this.colors[index % this.colors.length] : undefined,
                radius: index >= 0 ? 10 : 6,
                lineColor: 'red',
                lineWidth: 2
              }
            })
            // y2
            dataY2.push({
              x: self.$options.filters.epoch(m.MeasurementDateTime, m.MeasurementId),
              y: m[propY2],
              measurementId: m.MeasurementId
            })
          }
        }
      })
      dataY2 = dataY2.sort((a, b) => {
        return a.x - b.x
      })
      this.chartOptions.series[0].data = dataNormal
      this.chartOptions.series[1].data = dataWarning
      this.chartOptions.series[2].data = dataAlarm
      this.chartOptions.series[3].data = dataY2
      this.chartOptions.series[3].name = this.filterSettings.y2List[this.filterSettings.y2].label
    },
    formatYAxes () {
      this.chartOptions.yAxis[0].title.text = this.filterSettings.y1List[this.filterSettings.y1].label
      if (this.filterSettings.y1 === 0) {
        this.chartOptions.yAxis[0].categories = [this.$t('views.switch.directionnormal'), this.$t('views.switch.directionreverse')]
        this.chartOptions.yAxis[0].reversed = true
      } else {
        this.chartOptions.yAxis[0].categories = null
        this.chartOptions.yAxis[0].reversed = false
      }
      this.chartOptions.yAxis[1].title.text = this.filterSettings.y2List[this.filterSettings.y2].label
      if (this.filterSettings.y2 === 0) {
        // line
        this.chartOptions.series[3].type = 'spline'
        this.chartOptions.series[3].marker = {
          enabled: false
        }
      } else {
        // scatter dots
        this.chartOptions.series[3].type = 'scatter'
        this.chartOptions.series[3].marker = {
          enabled: true,
          symbol: 'circle',
          radius: 3,
          fillColor: 'blue'
        }
      }
    },
    switchSelected (id) {
      let index = this.selected.findIndex(s => s.id === id)
      if (index === -1) {
        this.addSelected(id)
      } else {
        this.removeSelected(id)
      }
    },
    selectAllVisible () {
      this.selected.forEach(s => {
        this.$emit('on-reset')
      })
      this.visible.forEach(v => {
        this.addSelected(v.MeasurementId)
      })
    },
    clearSelection () {
      this.$emit('on-reset')
    },
    showModal () {
      this.$modal.show('date-picker')
    },
    changeTimeFrame (timeFrame) {
      this.loading = true
      this.$emit('on-change-timeframe', timeFrame)
    },
    prevFrame () {
      this.changeTimeFrame({
        start: moment(this.timeFrame.start).subtract(this.span, 'd'),
        end: moment(this.timeFrame.start)
      })
      this.selectedPeriod = 'user_input'
    },
    lastFrame () {
      this.changeTimeFrame({
        start: moment().add(-7, 'days'),
        end: moment()
      })
      this.selectedPeriod = 'user_input'
    },
    nextFrame () {
      this.changeTimeFrame({
        start: moment(this.timeFrame.end),
        end: moment.min(moment(), moment(this.timeFrame.end).add(this.span, 'd'))
      })
      this.selectedPeriod = 'user_input'
    },
    addSelected (id) {
      this.$emit('on-select', id)
    },
    removeSelected (id) {
      this.$emit('on-deselect', id)
    },
    datePickerChanged () {
      this.selectedPeriod = 'user_input'
    },
    getStartDate (endDate) {
      switch (this.selectedPeriod) {
        case '1week':
          return moment(endDate).subtract(1, 'weeks')
        case '2weeks':
          return moment(endDate).subtract(2, 'weeks')
        case '1month':
          return moment(endDate).subtract(1, 'months')
        case '3months':
          return moment(endDate).subtract(3, 'months')
        case '6months':
          return moment(endDate).subtract(6, 'months')
        case '1year':
          return moment(endDate).subtract(1, 'years')
        default:
          return moment(endDate).subtract(1, 'months')
      }
    }
  },
  watch: {
    'filterSettings.normal' () {
      this.formatData()
    },
    'filterSettings.reverse' () {
      this.formatData()
    },
    'filterSettings.y1' () {
      this.formatData()
      this.formatYAxes()
    },
    'filterSettings.y2' () {
      this.formatData()
      this.formatYAxes()
    },
    selected () {
      this.formatData()
    },
    dateModel () {
      this.changeTimeFrame(this.dateModel)
    },
    timeFrame () {
      this.dateModel = this.timeFrame
    },
    switchObject () {
      this.selectedPeriod = moment(this.timeFrame.end).isSame(moment(), 'day') ? '1week' : 'user_input'
    },
    selectedPeriod (period) {
      if (period === 'user_input') {
        return
      }
      const end = moment().endOf('day')
      const start = this.getStartDate(end)
      this.changeTimeFrame({
        start: start,
        end: end
      })
    },
    measurements: {
      immediate: true,
      deep: true,
      handler () {
        // distribute data over Normal, Warning, Alarm
        let normalMeasurements = []
        let warningMeasurements = []
        let alarmMeasurements = []
        let comments = []
        this.measurements.forEach(m => {
          if (m.Comment) {
            comments.push({
              measurementId: m.MeasurementId,
              dateTime: this.$options.filters.epoch(m.MeasurementDateTime, m.MeasurementId),
              // dateTime: m.MeasurementDateTime,
              comment: m.Comment,
              direction: m.Direction
            })
          }
          switch (m.SeverityId) {
            case 1: {
              // normal
              normalMeasurements.push(m)
              break
            }
            case 2: {
              // warning
              warningMeasurements.push(m)
              break
            }
            case 3: {
              // alarm
              alarmMeasurements.push(m)
              break
            }
            default: {
              normalMeasurements.push(m)
            }
          }
        })
        this.normalMeasurements = normalMeasurements
        this.warningMeasurements = warningMeasurements
        this.alarmMeasurements = alarmMeasurements
        this.comments = comments
        this.formatData()
        this.checkChartLoad()
        this.loading = false
      }
    },
    refHistory () {
      let lines = []
      this.refHistory.forEach(r => {
        lines.push({
          value: moment(r.DateTime).valueOf(),
          color: '#FF0',
          width: 2,
          label: {
            rotation: 0,
            verticalAlign: 'top',
            text: this.$t('switch.referenceChanged')
          }
        })
      })
      this.chartOptions.xAxis.plotLines = lines
    },
    graphMode () {
      localStorage.graphMode = this.graphMode
      this.$emit('on-graph-mode-changed')
    }
  },
  mounted () {
    this.formatYAxes()
    if (localStorage.graphMode !== 'combined' &&
      localStorage.graphMode !== 'split') {
      localStorage.graphMode = 'combined'
    }
    this.graphMode = localStorage.graphMode
  }
}
</script>

<style scoped>
select {
  height: 2.5rem;
}

.actionLink {
  cursor: pointer;
}

.popover-container {
  display: inline-block;
}

.redo {
  transform: rotate(90deg);
}

.vue-highcharts-wrapper {
  min-height: 150px;
  display: inline-block;
  width: 100%;
}
.cssChart
{
  position:relative;
  height:250px;
  width:100%;
}
.body1 {
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  line-height: 20px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
}

.title {
  font-family: Roboto;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  font-size: 20px;
  line-height: 23px;
  color: rgba(0, 0, 0, 0.87);
}

.filter-label {
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  line-height: 20px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.4);
}

.horizontal-list {
  list-style-type: none;
  margin: 0;
  padding: 0;
}

.horizontal-list-item {
  display: inline-block;
}

.filter-graph-list {
  display: inline-block;
}

.filter-graph-list-item {
  padding-right: 30px;
}

.buttonbg {
  background: #56CCF2;
  box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.24), 0px 0px 2px rgba(0, 0, 0, 0.12);
  border-radius: 2px;
}

.button-frame {
  padding: 0 8px;
}

[type="checkbox"]:checked+span:not(.lever):before {
  height: 17px !important;
  width: 9px !important;
  top: 0px !important;
  left: 0px !important;
}

[type="checkbox"]+span:not(.lever) {
  font-size: 14px !important;
}

.periodSelector>>>select {
  padding: 0;
}

.graph-mode-selector {
  color: deepskyblue;
  border: none;
  padding: 0 16px;
}

.graph-mode-selector-button {
  border: deepskyblue 1px solid;
  border-radius: 5px;
  display: inline-flex;
  align-items: center;
  height: 2.5rem;
  padding: 0;
  box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.24), 0px 0px 2px rgba(0, 0, 0, 0.12);
}

.graph-mode-selector-button:hover {
  box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.24), 0px 0px 2px rgba(0, 0, 0, 0.12);
}

.graph-mode-selector:hover {
  cursor: pointer;
}
.csstooMuch {
    position:relative;
    height:255px;
    width:100%;
    background-color:white;
    color:red;
    padding:50px;
}

.chart-container {
  position: relative;
}

.spinner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  pointer-events: none;
}

/* Override Spinner.vue's fixed position when used in overlay (scoped deep) */
.spinner-overlay :deep(.spinner-element) {
  position: static !important;
  top: auto !important;
  left: auto !important;
  transform: none !important;
}
</style>



// WEBPACK FOOTER //
// src/components/switch/TimeSelector.vue